---
title: Úvod
sidebar_position: 1
description: Co je Planet Area Monitoring, základní pojmy, předpoklady a přístup.
---

# Úvod

## Co je Planet Area Monitoring Service?

**Planet Area Monitoring Service (Planet AMS)** je cloudové řešení určené k monitorování a ověřování aktivit v průběhu času na zemědělských a jiných plochách. Služba kombinuje data z veřejných zdrojů dálkového průzkumu Země, jako jsou Sentinel-1 a Sentinel-2, s vysokorozlišovacími snímky Planet, včetně Analysis-Ready PlanetScope (ARPS) a Planetary Variables.

Planet AMS transformuje surové satelitní snímky do dvou klíčových typů analytických dat:

- **Signály**: analýza časových řad agregovaná pro každý zákres JŽ ze satelitních pozorování, která zachycuje, jak se podmínky na poli mění v průběhu sezóny
- **Indikátory**: predikce jevů, jako jsou holá půda, klasifikace plodin a události sekání, odvozené ze vzorců fenologie signálů

Tato data jsou prezentována prostřednictvím **Area Monitoring Application (AM App)** — specializované webové aplikace, která nabízí intuitivní rozhraní kombinující snímky, signály, indikátory, hodnocení, rozhodnutí (semaforové indikátory) a pomocná data pro okamžité terénní ověření a kontrolu souladu.

![AM App](../imgs/am-app.png)

## Klíčové pojmy

Před zahájením práce s AM App je důležité porozumět těmto základním pojmům:

### Rozsah

**Rozsah** definuje geografická data a časové intervaly dostupné pro monitorování. Výběrem rozsahu určujete:

- konkrétní geografickou oblast,
- sadu zákresů JŽ (Features of Interest) v dané oblasti,
- časová období, pro která jsou data k dispozici,
- které signály a indikátory se vypočítávají.

Výběr rozsahu omezuje všechny následné možnosti filtrování a určuje, k jakým datům máte přístup.

### Zákres JŽ (Feature of Interest)

**Zákres JŽ (Feature of Interest, FOI)** je základní geometrický prvek monitorovaný v systému. Ve většině případů představuje zákres JŽ zemědělský pozemek nebo parcelu. Každý zákres JŽ má:

- jedinečný identifikátor,
- geografické hranice (polygonová geometrie),
- přidružené atributy (typ plodiny, využití půdy, plocha apod.).

### Signály

**Signály** jsou časové řady měření odvozené ze satelitních pozorování. Pro každý zákres JŽ jsou satelitní data zpracována k výpočtu hodnot, jako jsou:

- odrazivost pásem (např. červené, zelené, modré, blízkého infračerveného),
- vegetační indexy (NDVI, EVI),
- obsah vlhkosti,
- hladina chlorofylu,
- další spektrální měření.

Signály poskytují průběžná monitorovací data ukazující, jak se podmínky na poli mění v čase.

### Indikátory

**Indikátory** jsou prediktivní měření, která poukazují na konkrétní způsoby hospodaření nebo charakteristiky pole. Příklady zahrnují:

- detekci holé půdy,
- klasifikaci plodin (identifikaci pěstované plodiny),
- detekci události sekání,
- hodnocení homogenity.

Indikátory jsou odvozeny ze vzorců fenologie signálů a pomáhají identifikovat klíčové zemědělské aktivity a stavy pole.

Na základní indikátory navazují dva specializované typy indikátorů:

- **Hodnoticí indikátory (Evaluation Markers)** využívají signály a indikátory k zodpovězení konkrétní otázky ano/ne, například „Bylo toto pole v červenci posekáno?".
- **Rozhodovací indikátory (Decision Markers)** kombinují více hodnoticích indikátorů v konfigurovatelném rozhodovacím stromě a vytvářejí semaforové hodnocení odpovídající obchodním pravidlům nebo kritériím souladu dané organizace.

### Kontext indikátorů

**Kontext indikátorů** sdružuje více výpočtů indikátorů, které mohou vznikat v průběhu vegetační sezóny. Přepínáním mezi různými kontexty indikátorů lze porovnávat různé výpočetní scénáře nebo časová období (např. měsíční iterace).

## Systémové požadavky

Pro práci s AM App potřebujete:

**Webový prohlížeč**:

- moderní webový prohlížeč (Chrome, Firefox, Edge, Safari),
- povolený JavaScript.

**Internetové připojení**:

- stabilní internetové připojení,
- doporučeno: širokopásmové připojení pro optimální výkon.

**Přístup k účtu**:

- účet Planet NEBO dedikované přihlašovací údaje Area Monitoring Service,
- příslušná oprávnění rozsahu (nastavuje správce).

**Zobrazení**:

- minimální doporučené rozlišení obrazovky: 1366x768,
- nepodporováno na malých obrazovkách (mobilní telefony).

## Přístupová práva a oprávnění

Přístup k datům v AM App je řízen oprávněními rozsahu:

- Rozsah `DEMO_FR23` je ve výchozím nastavení povolený pro všechny uživatele se standardním účtem Planet. Tento demonstrační rozsah umožňuje prozkoumat funkce aplikace.
- Pro přístup k dalším nebo projektově specifickým rozsahům je nutné o přístup požádat.

Oprávnění rozsahu určují:

- které zákresy JŽ si lze prohlížet,
- která časová období jsou k dispozici,
- které signály a indikátory se vypočítávají,
- přístup ke správcovským funkcím (pokud se vztahuje).
